/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Presentation vocabulary for value provenance (R4/R4b/R4d).
 *
 * Split out of `ProvenanceChip` so it is testable: the console has no
 * component-test setup (vitest includes `src/**\/*.test.ts` only, and
 * `@testing-library/react` is not a dependency), and the part worth pinning is the
 * vocabulary rather than the markup. `provenance-presentation.test.ts` asserts these
 * labels against the design system's own `BADGE_PROVENANCE` rather than a copy.
 */

import {
  isInferredDeclaration,
  isUnconfirmedGuess,
  type ValueOrigin,
  type ValueProvenance,
} from './parameter-editor.adapters';

/**
 * Labels are the design system's, verbatim. A surface that shortens "from template"
 * to "template" stops agreeing with the legend on the same page, and the legend is
 * what R4 offers instead of a tooltip per row.
 */
export const PROVENANCE_LABEL: Record<ValueProvenance, string> = {
  detected: 'detected',
  template: 'from template',
  inherited: 'inherited',
  runtime: 'runtime supplies',
  overridden: 'overridden here',
};

/**
 * `info` for the three where the system made a decision about the value, `neutral`
 * for the two that describe where the value lives rather than a judgement made.
 */
export const PROVENANCE_TONE: Record<ValueProvenance, 'info' | 'neutral'> = {
  detected: 'info',
  template: 'info',
  inherited: 'neutral',
  runtime: 'neutral',
  overridden: 'info',
};

/**
 * What each chip means, for the per-surface legend (R4).
 *
 * R4 offers one legend per surface **instead of** a tooltip per row, so these have
 * to carry the whole explanation — there is nowhere else to put it. They explain the
 * label rather than restating it: "from template" already says where it came from,
 * so the useful addition is that the catalog composed it and nobody wrote it for
 * this source, which is what makes it safe to remove.
 */
export const PROVENANCE_MEANING: Record<ValueProvenance, string> = {
  detected: 'read from a tag or sample you supplied',
  // **Two routes, one fact** (2026-09-24): a row copied from a template the operator applied, or one
  // the standard catalog composed. It named only the catalog, which was false of every row copied from
  // a template an operator had authored.
  template: 'supplied by a template or the standard catalog, not written for this source',
  inherited: 'comes from this source’s parent',
  runtime: 'no stored value — each request supplies one',
  // **Widened 2026-08-27 (#1117).** It said *"would inherit"*, which is only `P3a`'s relation —
  // and the ORTB body has a second one: a leaf sitting on a path the driver builds from the
  // session. Both are *the operator replaced a value that would otherwise have been given to
  // them*, and describing only the first would have made the chip wrong on whichever surface it
  // was reused for.
  overridden: 'replaces a value this source would otherwise be given',
};

/**
 * The legend lists only what is actually on screen. A legend explaining marks nobody
 * has is the same noise as chipping every value, and it is what makes an operator
 * stop reading legends.
 */
export function provenanceLegendFor(values: Array<ValueOrigin | undefined>): ValueProvenance[] {
  const present = new Set(values.map((v) => v?.kind).filter((v): v is ValueProvenance => !!v));
  return (Object.keys(PROVENANCE_LABEL) as ValueProvenance[]).filter((v) => present.has(v));
}

/**
 * **The mark on a detected mapping nobody has confirmed** (`#1001`).
 *
 * Not a sixth chip: the chip still says `detected`, which is still true. This is a dimension of that
 * chip (`L3` — extensible in *fields*, closed in *chips*), and it is the one mark on the row that asks
 * for an act, so it is louder than the quiet marks and worded as a request to look rather than as a
 * verdict on the value (#1045).
 */
export const UNCONFIRMED_GUESS_LABEL = 'check';

/** What the mark means, for the legend — which lists it only when one is on screen. */
export const UNCONFIRMED_GUESS_MEANING =
  'matched on part of a name, so it may be wrong — open the row and Apply to keep it';

/** Whether the legend should explain the mark. */
export function hasUnconfirmedGuess(values: Array<ValueOrigin | undefined>): boolean {
  return values.some(isUnconfirmedGuess);
}

/**
 * **The row's own sentence about its guess** — `null` where there is no guess to confirm.
 *
 * It names the two ways out and, where only one exists, says why the other does not. A missing
 * Revert beside a mark asking the operator to check reads as a broken control; *the sample's value
 * belonged to a viewer and was not kept* reads as the reason, and it is the true one (`#952`, `#1188`).
 */
export function describeUnconfirmedGuess(origin: ValueOrigin | undefined): string | null {
  if (!origin || !isUnconfirmedGuess(origin)) return null;
  const evidence = origin.confidence === 'medium' ? 'on part of this field’s name' : 'on weak evidence';
  const base = `We matched this mapping ${evidence}, so it may be wrong. Open the row and Apply to keep it`;
  const sample = origin.sample;
  if (sample && 'value' in sample) return `${base}, or revert to send what the sample carried.`;
  if (sample && 'withheld' in sample) {
    return sample.withheld === 'discard-consent'
      ? `${base}. There is nothing to revert to: a consent string from a sample is never stored.`
      : `${base}. There is nothing to revert to: the sample’s value belonged to a viewer and was not kept.`;
  }
  return `${base}.`;
}

/** What pressing Revert sends — the outcome, quoted, rather than the mechanism. */
export function describeRevertDetection(value: string | number | boolean): string {
  return `Send what the sample carried, ${JSON.stringify(value)}, instead of this mapping.`;
}

/**
 * **What reverting a row changed from its template does** (Todd, 2026-09-24). The source is a copy,
 * so this is the template's **current** row, which may differ from what was copied — said, because
 * *"they may not remember (or have visibility into) what the template used to be"*.
 */
export function describeRevertToTemplate(templateName: string): string {
  return `Use what ${templateName} has for this row now, in place of your change. Saved with the rest of the source.`;
}

/**
 * **A template field whose declaration is a guess** (`D40`, `1f-iii`) — the mark beside a `template`
 * chip whose grade is `inferred`.
 *
 * A dimension of the chip, not a sixth one (`L3`), and quieter than `check`: it asks for nothing, it
 * says what we do not know. Worded about the **declaration**, because that is what was guessed — the
 * operator's own value on the row is not in question.
 */
export const INFERRED_DECLARATION_LABEL = 'inferred';

/** What the mark means, for the legend — which lists it only when one is on screen. */
export const INFERRED_DECLARATION_MEANING =
  'the template’s description and type for this field are our reading of the vendor’s naming, not checked against the vendor';

/** Whether the legend should explain the mark. */
export function hasInferredDeclaration(values: Array<ValueOrigin | undefined>): boolean {
  return values.some(isInferredDeclaration);
}

/** The row's own sentence about the grade — `null` where the declaration is not a guess. */
export function describeInferredDeclaration(origin: ValueOrigin | undefined): string | null {
  if (!isInferredDeclaration(origin)) return null;
  return 'The template’s description and type for this field are our reading of the vendor’s naming, not checked against the vendor. If you know better, your value and your note stand; the finding it raises never blocks a save.';
}
